/**
 * GET /v1/projects/{project_id}/history 와이어 타입.
 * 계약 SoT 는 backend-api Skill 라이브 조회(timeline_v1_projects__project_id__history_get).
 * action·history.type 은 서버가 열린 어휘로 선언한 필드라 방어적으로 string 으로 둔다
 * (유니온으로 좁히면 종류 추가 시 파싱이 깨진다 — design.md D7).
 */

/**
 * 알려진 history_type 18종 — 필터 옵션·라벨 매핑의 어휘 SoT. 선언 순서가 필터 옵션 순서이며 백엔드 어휘
 * 선언 순서와 같다(폴더는 설계도 바로 뒤, 장비·시설은 폴더 바로 뒤).
 * 서버 어휘 추가 시 여기에 더한 뒤 `HISTORY_TYPE_LABEL_KEYS`(타입이 누락을 강제한다)와
 * `myPage.history.type.*` i18n 리소스를 함께 갱신한다.
 * 백엔드 어휘와의 대조는 라이브 계약으로 한다 — `bun .claude/scripts/api-spec.mjs schema HistoryTarget` 의
 * `type` 설명이 백엔드 HISTORY_TYPES 를 선언 순서대로 나열한다. 웹에 없는 값은 raw 문자열로 표시되므로
 * 어휘를 손볼 때마다 한 번 대조한다(2026-10-22: `project_equipment` 만 빠져 있었다).
 */
export const KNOWN_HISTORY_TYPES = [
  "project",
  "time_project",
  "member",
  "annotation",
  "annotation_comment",
  "annotation_comment_image",
  "category",
  "vector",
  "clipping",
  "worksite_zone",
  "geolog",
  "walk_session",
  "layer",
  "drawing_mesh",
  "folder",
  "project_equipment",
  "height_profile_record",
  "height_profile_export",
] as const;

export type KnownHistoryType = (typeof KNOWN_HISTORY_TYPES)[number];

/**
 * 대상 종류 판정 — `type` 은 열린 어휘(string)라 리터럴 비교(`=== "member"`)는 오타가 나도 컴파일된다.
 * 두 번째 인자를 `KnownHistoryType` 으로 좁혀, 어휘 SoT(`KNOWN_HISTORY_TYPES`)에 없는 이름은 컴파일에서 잡는다.
 */
export const hasHistoryType = (
  target: Pick<HistoryTarget, "type">,
  type: KnownHistoryType
): boolean => target.type === type;

/** 행위자 스냅샷 — id 는 soft reference(유저 hard delete 후에도 기록 시점 번호 유지), name 은 기록 시점 스냅샷(탈퇴하면 빈 문자열). */
export interface HistoryActor {
  id: number | null;
  name: string;
}

/**
 * 대상 스냅샷 — id 는 soft reference(자연키·미등록 대상은 null), name 은 기록 시점 스냅샷.
 * annotation 의 name 은 stringified JSON({"title":…,"issue":…}) — 레거시 비정형 행은 원문.
 */
export interface HistoryTarget {
  /** 열린 어휘 — 알려진 값은 KNOWN_HISTORY_TYPES 참조 */
  type: string;
  id: number | null;
  name: string;
}

/** 시계열(회차) 귀속 — timestamp 는 촬영 일자(YYYY-MM-DD) 스냅샷, 누락 시 null. */
export interface HistoryTimeProjectRef {
  id: number;
  timestamp: string | null;
}

export interface HistoryEvent {
  /** 최신순 정렬·keyset cursor 축 */
  id: number;
  /** 기록 시각 (timestamptz ISO date-time) */
  occurred_at: string;
  actor: HistoryActor;
  /** create | update | delete — 열린 어휘, 방어적 string */
  action: string;
  history: HistoryTarget;
  /** 회차 귀속 대상만 채워진다 — 프로젝트·멤버 등 비귀속 이벤트는 null */
  time_project: HistoryTimeProjectRef | null;
}

export interface HistoryPage {
  /** id 내림차순(최신순) */
  items: HistoryEvent[];
  /** 다음 페이지 cursor — 마지막 페이지면 null */
  next_cursor: number | null;
}
